import type {ReactElement, ReactNode} from "react";
import {IconWrapper, type IconWrapperColor} from "../IconWrapper/IconWrapper";
import {Chip, LinearProgress, Paper, Stack, Typography} from "@mui/material";

export type StatsCardItem = {
    icon: ReactElement;
    iconColor: IconWrapperColor;
    label: string;
    value?: string;
    progress?: {done: number; total: number};
    tagText: ReactNode;
    onTagClick?: () => void;
};

export function StatsCard({items}: {items: StatsCardItem[]}) {
    return (
        <Stack direction="row" sx={{border: '1px solid', borderColor: 'grey.100', borderRadius: 1, overflow: 'hidden'}}>
            {items.map((item) => (
                <Paper
                    variant="elevation"
                    elevation={0}
                    key={item.label}
                    sx={{
                        p: 2,
                        borderRadius: 0,
                        borderWidth: '0.5px',
                        width: `calc(100% / ${items.length})`,
                        '&:not(:last-child)': {borderRight: '1px solid', borderColor: 'grey.100'}
                    }}
                >
                    <Stack gap={1.5} sx={{height: '100%'}}>
                        <Stack direction="row" gap={1} alignItems="center">
                            <IconWrapper size="small" color={item.iconColor}>
                                {item.icon}
                            </IconWrapper>
                            <Typography variant="caption" color="grey.600" fontWeight={500}>{item.label}</Typography>
                        </Stack>
                        {item.value && <Typography variant="h5">{item.value}</Typography>}
                        {item.progress && <Stack gap={1.5}>
                            <Stack direction="row" gap={0.25} alignItems="flex-end">
                                <Typography color={`${item.iconColor}.dark`} component="span" fontSize={26} sx={{height: '33px'}}>{item.progress.done}</Typography>
                                <Typography color="grey.600" component="span" fontSize={15}>/</Typography>
                                <Typography color="grey.600" component="span" fontSize={15}>{item.progress.total}</Typography>
                            </Stack>
                            <LinearProgress
                                color={item.iconColor === 'default' ? 'inherit' : item.iconColor}
                                variant="determinate"
                                value={(item.progress.done / item.progress.total) * 100}
                            />
                        </Stack>}
                        <Chip
                            size="small"
                            label={item.tagText}
                            onClick={item.onTagClick}
                            sx={{
                                alignSelf: 'flex-start',
                                marginTop: 'auto',
                                ...(item.onTagClick ? {cursor: 'pointer'} : {}),
                            }}
                        />
                    </Stack>
                </Paper>
            ))}
        </Stack>
    );
}
